import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Users, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../utils/dateLocale';

const EventCard = ({ event }) => {
  const { t } = useTranslation();
  const eventDate = new Date(event.date);
  const spotsLeft = event.max_participants - event.current_participants;
  const isShowcase = event._isShowcase;

  const cardContent = (
    <>
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={event.image_url || 'https://images.unsplash.com/photo-1766970096430-204f27f6e247?w=800'}
          alt={event.title}
          className="event-image w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className="badge badge-brand">
            {t(`sports.${event.sport_type}`, event.sport_type)}
          </span>
        </div>
        {isShowcase ? (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider bg-amber-400 text-asphalt rounded-sm shadow-md" data-testid="showcase-badge">
              <Sparkles className="w-3 h-3" />
              Exemple
            </span>
          </div>
        ) : (
          <>
            {spotsLeft <= 10 && spotsLeft > 0 && (
              <div className="absolute top-3 right-3">
                <span className="badge badge-warning">
                  {spotsLeft} {t('events.spotsLeft')}
                </span>
              </div>
            )}
            {spotsLeft === 0 && (
              <div className="absolute top-3 right-3">
                <span className="badge bg-red-500 text-white">
                  {t('events.full')}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow">
        {/* Date badge */}
        <div className="flex items-center mb-3">
          <div className="bg-asphalt text-white px-3 py-1 text-center mr-3">
            <div className="font-heading font-bold text-xl leading-none">
              {format(eventDate, 'd')}
            </div>
            <div className="font-heading text-xs uppercase tracking-wider">
              {format(eventDate, 'MMM', { locale: getDateLocale() })}
            </div>
          </div>
          <div className="text-sm text-slate-500">
            {format(eventDate, 'yyyy')}
          </div>
        </div>

        {/* Title */}
        <h3 className="font-heading font-bold text-lg text-asphalt group-hover:text-brand transition-colors mb-2 line-clamp-2">
          {event.title}
        </h3>

        {/* Location */}
        <div className="flex items-center text-sm text-slate-500 mb-3">
          <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
          <span className="truncate">{event.location}</span>
        </div>

        {/* Distances */}
        {event.distances && event.distances.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {event.distances.slice(0, 3).map((distance, idx) => (
              <span key={idx} className="distance-badge">
                {distance}
              </span>
            ))}
            {event.distances.length > 3 && (
              <span className="distance-badge bg-slate-200 text-slate-600">
                +{event.distances.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto pt-4 border-t flex items-center justify-between">
          <div className="flex items-center text-sm text-slate-500">
            <Users className="w-4 h-4 mr-1" />
            <span>{event.current_participants}/{event.max_participants}</span>
          </div>
          <div className="price-tag">
            {t('events.fromPrice')} {event.price}€
          </div>
        </div>

        {/* Showcase CTA */}
        {isShowcase && (
          <div className="mt-3 pt-3 border-t border-dashed border-amber-300 text-center">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
              Creez votre evenement comme celui-ci
            </span>
          </div>
        )}
      </div>
    </>
  );

  if (isShowcase) {
    return (
      <Link
        to={`/events/${event.event_id}`}
        className="event-card card-event group ring-2 ring-amber-300/50 ring-offset-2"
        data-testid={`showcase-card-${event.event_id}`}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <Link
      to={`/events/${event.event_id}`}
      className="event-card card-event group"
      data-testid={`event-card-${event.event_id}`}
    >
      {cardContent}
    </Link>
  );
};

export default EventCard;
